import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { GridOptions, GridApi, GridReadyEvent,SelectionChangedEvent, ICellRendererParams } from 'ag-grid-community';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisTimePipe } from '@iris/common/modules/time';
import { IrisFileSizePipe } from '@iris/common/modules/pipes-common/pipes/filesize/file-size.pipe';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { filter, take, tap } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { isEqual, uniqBy } from 'lodash';
import { IrisDmsContentPageFilterI } from '../../../models/IrisDmsContentPagination';
import { IrisEmptyContentViewComponent } from '../../empty-content-view/empty-content-view.component';
import { IrisGridMode } from '@iris/common/modules/grid/ag-grid/models/iris-grid-mode';
import { PageChangedEvent } from '@iris/common/modules/grid/ag-grid/extensions/pagination/page-changed-event';
import { IrisQueryParamsOrderBy } from '@iris/api-query';
import { debounce } from '@iris/common/decorators/debounce.decorator';
import { IrisDmsModalFilesStore } from '../../../store/modal-files.signal-store';
import { SelectFilesModalParamsI } from '../../../sandbox/dms-modals.sandbox';
import { IrisDmsMatFoldersTreeComponent } from '../../folders-structures/dms-mat-folders-tree/dms-mat-folders-tree.component';
import { IrisFileUploaderComponent } from '../../file-uploader/file-uploader.component';
import { IrisTileViewLiteComponent } from '../../tile-view/tile-view-lite/tile-view-lite.component';
import { AgGridModule } from 'ag-grid-angular';
import { AgGridPaginationModule } from '@iris/common/modules/grid/ag-grid/extensions/pagination/pagination.module';
import { FormsModule } from '@angular/forms';
import { StrakitButtonComponent, StrakitSelectComponent } from '@ngx-strakit/cdk/controls';
import { IrisProjectSkSelectDirective } from '@iris/common/modules/fields/project-sk-select/project-select.directive';
import { IrisGridModule } from '@iris/common/modules/grid/grid.module';
import { IrisSkSelectFieldDirective } from '@iris/common/modules/fields/sk-select-field/ng-select-field.sk.directive';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
  StrakitButtonSuffixDirective,
} from '@ngx-strakit/cdk';
import {
  Directions,
  ResizeEventI,
  StrakitResizableComponent,
} from '@ngx-strakit/cdk/layout/resizable';

@Component({
  selector: 'iris-select-files-modal',
  imports: [
    TranslatePipe,
    FormsModule,

    AgGridModule,
    AgGridPaginationModule,

    IrisDmsMatFoldersTreeComponent,
    IrisFileUploaderComponent,
    IrisTileViewLiteComponent,
    IrisEmptyContentViewComponent,

    IrisSkSelectFieldDirective,
    ConfirmByKeypressDirective,
    StrakitSelectComponent,
    IrisProjectSkSelectDirective,
    IrisGridModule,
    SkModalContentComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
    StrakitButtonSuffixDirective,
    StrakitResizableComponent,
  ],
  styleUrls: ['./select-files-modal.component.scss'],
  templateUrl: './select-files-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisSelectFilesModalComponent extends SkModalBase<IrisSelectFilesModalComponent, IrisDMSFileI[]> implements OnInit, OnDestroy {
  private readonly irisTimePipe = inject(IrisTimePipe);
  private readonly irisFileSizePipe = inject(IrisFileSizePipe);
  private readonly translateService = inject(TranslateService);
  private readonly filesService = inject(IrisFilesService);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly data = inject<SelectFilesModalParamsI>(MAT_DIALOG_DATA);

  private readonly dmsModalFilesStore = inject(IrisDmsModalFilesStore);

  readonly multiple = signal(false);
  readonly showProjectSelector = signal(false);
  readonly selectedFiles = signal<IrisDMSFileI[]>([]);
  readonly selectedFilesLength = computed(() => this.selectedFiles().length);
  readonly hasSelectedFiles = computed(() => !!this.selectedFilesLength());

  readonly showTileView = signal(false);
  readonly showFileUploader = signal(true);

  readonly projectId = signal(this.dmsFoldersSandbox.getCurrentProjectId(true));
  readonly projectInfoMessage = computed(() =>
    !this.projectId() ? 'text.dms.PleaseSelectProject' : 'text.dms.ThisProjectDoNotHaveFolders',
  );

  private gridApi!: GridApi<IrisDMSFileI>;

  readonly pageSize = 25;
  readonly gridMode = IrisGridMode.Server;

  readonly gridOptions: GridOptions<IrisDMSFileI> = {
    getRowId: (params) => params.data.id,
    pagination: true,
    paginationPageSize: this.pageSize,
    noRowsOverlayComponent: IrisEmptyContentViewComponent,
    noRowsOverlayComponentParams: {
      emptyContentLabel: 'text.dms.NoFilesFound',
      emptyContentIconClass: 'fa-filter',
    },
    defaultColDef: {
      comparator: () => 0,
    },
    columnDefs: [
      {
        headerName: this.translateService.instant('label.Name'),
        field: 'name',
        minWidth: 100,
        flex: 1,
        cellRenderer: (param: ICellRendererParams<IrisDMSFileI>) =>
          `<div class="ui-grid-cell-contents actions">
              <i class="fas ${param.data.isLocked ? 'fa-lock' : this.filesService.getIcon(param.data.mimeType)}"></i> ${param.value}
           </div>`,
      },
      {
        headerName: this.translateService.instant('label.UpdatedOn'),
        field: 'updatedOn',
        width: 100,
        valueFormatter: (param) => this.irisTimePipe.transform(param.value),
      },
      {
        headerName: this.translateService.instant('label.Size'),
        field: 'size',
        width: 100,
        valueFormatter: (param) => this.irisFileSizePipe.transform(param.data.size, 1),
      },
    ],
    onGridReady: (event: GridReadyEvent<IrisDMSFileI>) => this.onGridReady(event),
    onRowDataUpdated: () => {
      setTimeout(() => this.setSelectedFiles());
    },
    onSortChanged: () => this.onSortChanged(),
  };

  readonly total = this.dmsModalFilesStore.total;
  private readonly pageNumber = this.dmsModalFilesStore.pageNumber;
  readonly page = computed(() => this.pageNumber() + 1);
  private readonly orderBy = this.dmsModalFilesStore.orderBy;

  readonly showFoldersTree = signal(true);
  readonly resizeDirections = computed(() => [!this.showFoldersTree() ? Directions.None : Directions.Right]);
  readonly rawFoldersTreeWidth = signal(300);
  readonly foldersTreeWidth = computed(() => `${this.rawFoldersTreeWidth()}px`);
  readonly foldersTreeMarginLeft = computed(() => {
    return this.showFoldersTree() ? '0' : `-${this.rawFoldersTreeWidth() + 16}px`;
  });

  readonly toggleFoldersTreeIconClass = computed(() =>
    this.showFoldersTree() ? 'fa-chevron-double-left' : 'fa-chevron-double-right',
  );

  readonly hasFolders = this.dmsFoldersSandbox.hasFolders;
  readonly selectedFolderId = this.dmsFoldersSandbox.selectedFolderIdModal;

  readonly hasNextPage = this.dmsModalFilesStore.hasNextPage;
  readonly pagination = this.dmsModalFilesStore.pagination;

  readonly files = this.dmsModalFilesStore.files;
  readonly filesLoading = this.dmsModalFilesStore.loading;
  readonly filesLoading$ = toObservable(this.filesLoading);

  private readonly rootFolder = this.dmsFoldersSandbox.currentProjectRootFolder;
  private readonly rootFolder$ = this.dmsFoldersSandbox.currentProjectRootFolder$;
  readonly showProjectInfoMessage = computed(() => !this.rootFolder());

  ngOnInit(): void {
    this.multiple.set(this.data.multiple);
    this.showProjectSelector.set(this.data.showProjectSelector);
    this.selectedFiles.set(this.data.selectedFiles ?? []);
    this.showTileView.set(this.data.showTileView ?? this.showTileView());
    this.showFileUploader.set(this.data.showFileUploader ?? this.showFileUploader());

    this.dmsModalFilesStore.setViewType(this.showTileView());

    this.gridOptions.rowSelection = {
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
      headerCheckbox: false,
      mode: this.multiple() ? 'multiRow' : 'singleRow',
    };

    this.dmsFoldersSandbox.selectedFolderIdModal$.pipe(
      take(1),
      tap((selectedFolderId) => {
        const rootFolder = this.rootFolder();
        if (selectedFolderId && selectedFolderId !== rootFolder?.id) {
          this.dmsFoldersSandbox.getParentFolders(selectedFolderId, true);
        } else if(!selectedFolderId && rootFolder) {
          this.dmsFoldersSandbox.setSelectedFolderModal(rootFolder.id, true);
        }
      }),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.dmsModalFilesStore.resetFiles();
    super.ngOnDestroy();
  }

  onGridReady(event: GridReadyEvent<IrisDMSFileI>): void {
    this.gridApi = event.api;
    this.applyOrderBy(this.orderBy());
  }

  setSelectedFiles(): void {
    if (!this.gridApi) {
      return;
    }
    const filesIds = this.selectedFiles().map(file => file.id);
    this.gridApi.forEachNode((node) => {
      if (filesIds.includes(node.data.id) && !node.isSelected()) {
        node.setSelected(true);
      }
    });
  }

  onFileSuccessUpload(response: IrisDMSFileI[]): void {
    const file = response[0];
    this.dmsModalFilesStore.addFiles([file]);
    if (this.multiple()) {
      this.selectedFiles.update(files => files.concat([file]));
    } else {
      this.selectedFiles.set([file]);
    }
  }

  onSelect(): void {
    if (!this.hasSelectedFiles()) {
      return;
    }
    this.closeWithResult(this.selectedFiles());
  }

  selectedProjectChanged(projectId: number): void {
    if ((!!projectId || projectId === null) && this.projectId() !== projectId) {
      this.projectId.set(projectId);
      this.dmsFoldersSandbox.changeProjectFolderModal(projectId);
      this.dmsFoldersSandbox.getProjectRootFolder(projectId);
      this.selectRootFolderOnProjectChange();
    }
  }

  onRowSelectionChange(event: SelectionChangedEvent<IrisDMSFileI>): void {
    const selectedBefore = this.selectedFiles().filter(file => file.parentId !== this.selectedFolderId());
    this.selectedFiles.set(uniqBy([...selectedBefore, ...event.api.getSelectedRows()], 'id'));
  }

  onTileViewRowSelectionChange(rowsData: IrisDMSFileI[]): void {
    const selectedBefore = this.selectedFiles().filter(file => file.parentId !== this.selectedFolderId());
    this.selectedFiles.set(uniqBy([...selectedBefore, ...(rowsData ?? []).filter(row => !row.isLocked)], 'id'));
  }

  toggleFoldersTree(): void {
    this.showFoldersTree.set(!this.showFoldersTree());
  }

  onScroll(): void {
    if (!this.hasNextPage()) {
      return;
    }
    const { limit, offset, orderBy, filter } = this.pagination();

    this.dmsModalFilesStore.requestFiles$({
      pageSize: limit,
      page: Math.ceil((offset + limit) / limit),
      orderBy,
      filter,
    });
  }

  onFilesFilterChanged(filter: IrisDmsContentPageFilterI): void {
    const { limit, orderBy } = this.pagination();

    this.dmsModalFilesStore.requestFiles$({
      pageSize: limit,
      page: 0,
      orderBy,
      filter,
    });
  }

  contentPageChanged(event: PageChangedEvent): void {
    const orderBy = this.getCurrentOrderBy();
    const { page, pageSize } = event;
    const { filter } = this.pagination();

    this.dmsModalFilesStore.requestFiles$({
      pageSize,
      page: page - 1,
      orderBy,
      filter,
    });
  }

  @debounce(1000)
  onSortChanged(): void {
    const orderBy = this.getCurrentOrderBy();

    if (isEqual(orderBy, this.orderBy())) { return; }

    this.dmsModalFilesStore.requestFiles$({
      pageSize: this.pageSize,
      page: this.gridApi.paginationGetCurrentPage(),
      orderBy,
    });
  }

  public onResizeEnd(e: ResizeEventI): void {
    this.rawFoldersTreeWidth.set(e.width);
  }

  private getCurrentOrderBy(): IrisQueryParamsOrderBy[] {
    const columnStates = this.gridApi?.getColumnState().filter(state => !!state.sort) ?? [];
    return columnStates.map(columnState => {
      const colDef = this.gridApi?.getColumnDef(columnState.colId);

      return new IrisQueryParamsOrderBy({ name: colDef.field, value: columnState.sort });
    });
  }

  private applyOrderBy(orderBy: IrisQueryParamsOrderBy[] = []): void {
    this.gridApi?.applyColumnState({
      state: orderBy.map((_orderBy, i) => ({
        colId : _orderBy.name,
        sort: _orderBy.asc ? 'asc' : 'desc',
        sortIndex: i,
      })),
      defaultState: { sort: null },
    });
  }

  private selectRootFolderOnProjectChange(): void {
    this.rootFolder$.pipe(
      filter(Boolean),
      filter(rootFolder => rootFolder?.projectId === this.projectId()),
      take(1),
      tap(rootFolder => {
        this.dmsFoldersSandbox.setSelectedFolderModal(rootFolder.id, true);
        this.dmsFoldersSandbox.expandFolder(rootFolder.id);
      }),
    ).subscribe();
  }
}
